import { ladderInvalidReason, pctToGate } from './sniperArm';
import type { LadderBand, PatchSniperMarketDto } from '../types/sniper.types';

/**
 * A patch to apply to every selected market — only the fields the operator typed.
 * DERIVED from the wire DTO (not hand-duplicated) so the bulk bar can never drift
 * from the contract it writes to.
 */
export type SniperBulkPatch = Pick<
  PatchSniperMarketDto,
  | 'size'
  | 'maxPrice'
  | 'varExitPrice'
  | 'varDefenseEnabled'
  | 'varPreventiveExitEnabled'
  | 'varConfirmedFloorPrice'
  | 'varRebuyEnabled'
  | 'autoNoEnabled'
  | 'noMaxPrice'
  | 'noSize'
  | 'ladderEnabled'
  | 'ladderBands'
  | 'floorPrice'
  | 'varPreventiveExitOffset'
  | 'confidenceGate'
  | 'enabled'
>;

export interface ParsedBulkPatch {
  patch: SniperBulkPatch;
  /** At least one typed field is valid AND none typed is invalid. */
  canApply: boolean;
}

/**
 * The bulk bar's ladder staging value. `null` = leave the ladder untouched,
 * which is what the bar starts at — unlike the numeric fields there is no
 * "blank" string that could stand for it.
 */
export interface BulkLadderOverlay {
  ladderEnabled: boolean;
  ladderBands: LadderBand[];
  floorPrice: number | null;
  varPreventiveExitOffset: number;
}

/**
 * Parse the bulk-bar draft strings into a patch carrying ONLY the params the
 * operator typed and that are valid. A blank field is omitted (leave untouched).
 * Validation mirrors `SniperMarketRow` AND the backend DTO: size >= 1 (server is
 * `@Min(1)` — accepting a fractional size here only earns a 400 on the whole
 * bulk), maxPrice, varExitPrice and varConfirmedFloorPrice ∈ (0,1], gate a
<<<<<<< HEAD
 * percent 1..100 (→ 0..1 via `pctToGate`). The four VAR-defense/auto-NO
 * switches (`varDefenseEnabled` / `varPreventiveExitEnabled` /
 * `varRebuyEnabled` / `autoNoEnabled`) are tri-state on the wire: `''` =
 * leave untouched, `'true'`/`'false'` = set the boolean; any other string is
 * invalid. Any typed-but-invalid field blocks Apply so a bad value can't
 * silently skip. `noMaxPrice` (the auto-NO ceiling) validates like
 * `maxPrice` — (0,1]. `noSize` (the auto-NO leg's OWN contract size)
 * validates like `size` — `>= 1`. NOTE: unlike the per-row field, bulk
 * `noSize` blank means "untouched" (the bar's universal convention), NOT
 * "explicitly clear to inherit `size`" — clearing to inherit is a
 * per-row-only action for now.
 *
 * `ladder` is the one non-string param: `null` leaves the ladder untouched, an
 * overlay sets it. Turning it ON carries the rungs + floor (and must pass the
 * same `ladderInvalidReason` the row and the server enforce); turning it OFF
 * writes only the flag, leaving the rungs on the row for a later re-enable.
 */
export function parseBulkPatch(
  size: string,
  maxPrice: string,
  varExitPrice: string,
  gate: string,
  varDefenseEnabled: string = '',
  varPreventiveExitEnabled: string = '',
  varConfirmedFloorPrice: string = '',
  varRebuyEnabled: string = '',
  autoNoEnabled: string = '',
  noMaxPrice: string = '',
  noSize: string = '',
  ladder: BulkLadderOverlay | null = null,
): ParsedBulkPatch {
  const patch: SniperBulkPatch = {};
  let invalid = false;
  let any = false;

  if (size !== '') {
    const s = Number(size);
    if (Number.isFinite(s) && s >= 1) {
      patch.size = s;
      any = true;
    } else invalid = true;
  }
  if (maxPrice !== '') {
    const m = Number(maxPrice);
    if (Number.isFinite(m) && m > 0 && m <= 1) {
      patch.maxPrice = m;
      any = true;
    } else invalid = true;
  }
  if (varExitPrice !== '') {
    const v = Number(varExitPrice);
    if (Number.isFinite(v) && v > 0 && v <= 1) {
      patch.varExitPrice = v;
      any = true;
    } else invalid = true;
  }
  // Phase-2 aggressive-sell floor — a price like varExitPrice, so the SAME (0,1]
  // rule as the server's.
  if (varConfirmedFloorPrice !== '') {
    const f = Number(varConfirmedFloorPrice);
    if (Number.isFinite(f) && f > 0 && f <= 1) {
      patch.varConfirmedFloorPrice = f;
      any = true;
    } else invalid = true;
  }
  // Auto-NO ceiling — a price like maxPrice/varExitPrice, same (0,1] rule.
  if (noMaxPrice !== '') {
    const n = Number(noMaxPrice);
    if (Number.isFinite(n) && n > 0 && n <= 1) {
      patch.noMaxPrice = n;
      any = true;
    } else invalid = true;
  }
  // Auto-NO leg size — contracts, same `>= 1` rule as `size`.
  if (noSize !== '') {
    const ns = Number(noSize);
    if (Number.isFinite(ns) && ns >= 1) {
      patch.noSize = ns;
      any = true;
    } else invalid = true;
  }
  // Tri-state booleans: '' leaves untouched, only 'true'/'false' set the flag.
  const bools: Array<
    [
      string,
      (
        | 'varDefenseEnabled'
        | 'varPreventiveExitEnabled'
        | 'varRebuyEnabled'
        | 'autoNoEnabled'
      ),
    ]
  > = [
    [varDefenseEnabled, 'varDefenseEnabled'],
    [varPreventiveExitEnabled, 'varPreventiveExitEnabled'],
    [varRebuyEnabled, 'varRebuyEnabled'],
    [autoNoEnabled, 'autoNoEnabled'],
  ];
  for (const [raw, key] of bools) {
    if (raw !== '') {
      if (raw === 'true' || raw === 'false') {
        patch[key] = raw === 'true';
        any = true;
      } else invalid = true;
    }
  }
  if (gate !== '') {
    const g = pctToGate(Number(gate)); // 1..100 → 0..1, else null
    if (g !== null) {
      patch.confidenceGate = g;
      any = true;
    } else invalid = true;
  }
  if (ladder) {
    if (!ladder.ladderEnabled) {
      patch.ladderEnabled = false;
      any = true;
    } else if (
      ladderInvalidReason(
        ladder.ladderBands,
        ladder.floorPrice,
        ladder.varPreventiveExitOffset,
      ) === null &&
      ladder.floorPrice != null
    ) {
      patch.ladderEnabled = true;
      patch.ladderBands = ladder.ladderBands;
      patch.floorPrice = ladder.floorPrice;
      patch.varPreventiveExitOffset = ladder.varPreventiveExitOffset;
      any = true;
    } else invalid = true;
  }

  return { patch, canApply: any && !invalid };
}
